import * as React from "react";
import { observer } from "mobx-react";
import { useContext } from "react";
import Link from "next/link";
import { Button, Checkbox, HTMLTable, Icon, PopoverInteractionKind } from "@blueprintjs/core";

import OrderByIcon from "../../order-by-icon";
import GenomesTablePaginator from "../../paginator/genomes-table";
import { GENOMES_PER_PAGE } from "../../../utils/constants";
import { StoresContext } from "../../../pages/_app";
import { CollectionName, Genome } from "../../../api";
import LoadingRow from "../genomes/loading-row";
import { Popover2 } from "@blueprintjs/popover2";
import { MultipleValues } from "../../multiple-values";
import { cleanTaxaName } from "../../../utils/genomes";
import FavoriteStar from "../../favorite-star";

const NUM_HEADERS = 9;

const CellBioTable = observer(({}: { collectionName: CollectionName | null }) => {
  const rootStore = useContext(StoresContext);
  const {
    setPage,
    setOrderBy,
    orderBy,
    ascending,
    mineOnly,
    isItemSelected,
    toggleSelected,
    isPageSelected,
    togglePageSelected,
    toggleGenomeStarring,
  } = rootStore.cellBioStore;
  const { loading, genomes, pagination } = rootStore.cellBioStore.listData;

  const totalCount = pagination.total || genomes.length;
  const page = pagination.page || 1;
  const rows: React.ReactNode[] = [];
  const isLoggedIn = rootStore.userStore.isLoggedIn();

  if (loading) {
    for (let i = 0; i < GENOMES_PER_PAGE; ++i) {
      rows.push(<LoadingRow numColumns={NUM_HEADERS} key={i} />);
    }
  } else if (genomes.length === 0) {
    return (
      <div>
        <h3>{mineOnly ? "No Starred Cell Lines" : "No Available Products"}</h3>
      </div>
    );
  } else {
    for (const g of genomes) {
      const meta: Partial<Genome["attributes"]["atcc_metadata"]> = g.attributes.atcc_metadata ?? {};
      rows.push(
        <tr key={g.uuid}>
          <td>
            <Checkbox checked={isItemSelected(g.uuid)} onChange={() => toggleSelected(g.uuid)} />
          </td>
          <td className="genome-star">
            <FavoriteStar
              userLoggedIn={isLoggedIn}
              genome={g}
              toggleStarring={toggleGenomeStarring}
            />
          </td>
          <td className="taxon-name">
            <Popover2
              interactionKind={PopoverInteractionKind.HOVER}
              placement={"top"}
              content={"Click to go to overview page"}
              popoverClassName="info-tooltip"
            >
              <Link as={`/cell-bio/${g.uuid}`} href={"/cell-bio/[uuid]"}>
                <Button minimal>{meta.cell_line_name}</Button>
              </Link>
            </Popover2>
          </td>
          <td>
            {meta.product_url ? (
              <a target="_blank" rel="noreferrer" href={meta.product_url}>
                {g.name} <Icon icon="share" />
              </a>
            ) : (
              g.name
            )}
          </td>
          <td>{cleanTaxaName(g.taxon_name)}</td>
          <td>{meta.tissue ? <MultipleValues values={meta.tissue.split("|")} /> : null}</td>
          <td>{meta.disease ? <MultipleValues values={meta.disease.split("|")} /> : null}</td>
          <td>
            {new Date(g.created_at).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </td>
        </tr>
      );
    }
  }

  return (
    <>
      <HTMLTable className="genomes-table" striped={true}>
        <thead>
          {/* Do not forget to change NUM_HEADERS when adding/removing headers */}
          <tr>
            <th className="genomes-table__checkbox">
              <Checkbox
                checked={isPageSelected(genomes.map((g) => g.uuid))}
                onChange={() => togglePageSelected(genomes.map((g) => g.uuid))}
              />
            </th>
            <th className="genomes-table__star" />

            <th className="clickable" onClick={() => setOrderBy("cell_line_name")}>
              Cell Line Name
              <OrderByIcon isActive={orderBy === "cell_line_name"} isAscending={ascending} />
            </th>
            <th className="clickable" onClick={() => setOrderBy("name")}>
              ATCC Product Page
              <OrderByIcon isActive={orderBy === "name"} isAscending={ascending} />
            </th>
            <th className="clickable" onClick={() => setOrderBy("taxon.name")}>
              Taxonomic Name
              <OrderByIcon isActive={orderBy === "taxon.name"} isAscending={ascending} />
            </th>
            <th className="clickable" onClick={() => setOrderBy("tissue")}>
              Tissue
              <OrderByIcon isActive={orderBy === "tissue"} isAscending={ascending} />
            </th>
            <th className="clickable" onClick={() => setOrderBy("disease")}>
              Disease
              <OrderByIcon isActive={orderBy === "disease"} isAscending={ascending} />
            </th>
            <th className="clickable" onClick={() => setOrderBy("created_at")}>
              Date Published
              <OrderByIcon isActive={orderBy === "created_at"} isAscending={ascending} />
            </th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </HTMLTable>
      <GenomesTablePaginator totalCount={totalCount} page={page} onPageChange={setPage} />
    </>
  );
});

export default CellBioTable;
